<script>
/**
 * Disparo rápido — fila única de envios pelos chips.
 *
 * A pessoa escreve a mensagem, escolhe a foto (opcional) e cola os números da
 * planilha. O envio entra no fim da fila e sai pelos chips de disparo
 * conectados, um número por chip, na ordem em que os envios entraram. Chip
 * novo entra no rodízio sozinho assim que conecta.
 *
 * A foto segue o caminho dos grupos: escolhida no navegador (prévia local),
 * sobe por multipart no campo "file" e vai no envio como imagem + legenda.
 */
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import socketClient from '@/services/socketClient';
import resolveUrl from '@/services/resolveUrl';
import { sucesso, erro, confirmar } from '@/helpers/common/api';
import { analisarNumeros } from '@/helpers/disparoNumeros';

const MAX_FOTO_MB = 15;
const TIPOS_FOTO = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_TEXTO = 4096;
const ATUALIZAR_MS = 5000;

const ANALISE_VAZIA = () => ({ validos: [], duplicados: 0, invalidos: 0, exemplosInvalidos: [] });

export default {
  name: 'DisparoRapido',
  components: { Layout, PageHeader },
  data() {
    return {
      MAX_TEXTO,
      MAX_FOTO_MB,
      form: { nome: '', texto: '', numeros: '' },
      // De onde vêm os números: 'lista' (colados/CSV) ou 'etiquetas' (contatos do sistema).
      origem: 'lista',
      etiquetas: [], // [{ id, nome, cor, contatos }]
      carregandoEtiquetas: false,
      erroEtiquetas: null,
      selecao: { incluir: [], excluir: [] },
      contagemEtiquetas: null, // { contatos, elegiveis, optOut, semTelefone }
      contandoEtiquetas: false,
      erroContagem: null,
      timerContagem: null,
      seqContagem: 0,
      foto: null, // { file, previewUrl, nome, tamanho }
      fotoErro: null,
      arrastando: false,
      analise: ANALISE_VAZIA(),
      timerAnalise: null,
      // Quantos da lista já são sabidamente sem WhatsApp (servidor).
      conferencia: null, // { validos, semWhatsapp, validadeDias }
      timerConferencia: null,
      seqConferencia: 0,
      mostrarInvalidos: false,
      enviando: false,
      etapa: '',
      painel: null,
      carregando: true,
      erroPainel: null,
      acaoEmAndamento: null,
      timer: null,
      timerSocket: null,
      offSocket: [],
    };
  },
  computed: {
    /** Quantos vão receber no modo escolhido (prévia; o servidor confirma). */
    totalDestinatarios() {
      if (this.origem === 'etiquetas') {
        return this.selecao.incluir.length && this.contagemEtiquetas ? this.contagemEtiquetas.elegiveis : 0;
      }
      return this.analise.validos.length;
    },
    podeEnviar() {
      return !this.enviando
        && (this.form.texto.trim() || this.foto)
        && this.totalDestinatarios > 0
        && !(this.origem === 'etiquetas' && this.contandoEtiquetas)
        && this.form.texto.length <= MAX_TEXTO;
    },
    opcoesIncluir() {
      return this.etiquetas.map((e) => ({ value: e.id, label: `${e.nome} · ${this.fmt(e.contatos)}` }));
    },
    /** Quem já está em "incluir" não aparece em "tirar". */
    opcoesExcluir() {
      const incluidas = new Set(this.selecao.incluir);
      return this.opcoesIncluir.filter((o) => !incluidas.has(o.value));
    },
    legendaLonga() {
      return !!this.foto && this.form.texto.length > 1024;
    },
    chips() {
      return (this.painel && this.painel.chips) || null;
    },
    /** Chips na ordem do rodízio: no rodízio (quem menos disparou hoje primeiro), depois o resto. */
    chipsOrdenados() {
      const peso = (c) => {
        if (c.conectado && !c.pausado) return 0;
        if (c.status === 'reconectando') return 1;
        if (c.pausado) return 2;
        return 3;
      };
      return [...((this.chips && this.chips.chips) || [])]
        .sort((a, b) => (peso(a) - peso(b)) || (a.enviadosHoje - b.enviadosHoje) || (a.id - b.id));
    },
    lotes() {
      return (this.painel && this.painel.lotes) || [];
    },
    fila() {
      return (this.painel && this.painel.fila) || { pendentes: 0, pendentesPausados: 0, lotesNaFila: 0 };
    },
    temPausados() {
      return this.lotes.some((l) => l.status === 'pausado');
    },
    horaAgora() {
      return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
  },
  watch: {
    origem(novo) {
      if (novo === 'etiquetas' && !this.etiquetas.length && !this.carregandoEtiquetas) this.carregarEtiquetas();
    },
    'selecao.incluir': {
      deep: true,
      handler() {
        // Etiqueta que passou a ser incluída sai de "tirar quem tiver".
        const incluidas = new Set(this.selecao.incluir);
        if (this.selecao.excluir.some((id) => incluidas.has(id))) {
          this.selecao.excluir = this.selecao.excluir.filter((id) => !incluidas.has(id));
        }
        this.agendarContagem();
      },
    },
    'selecao.excluir': {
      deep: true,
      handler() { this.agendarContagem(); },
    },
    'form.numeros': function analisarDepois() {
      // Colar 60 mil linhas e reanalisar a cada tecla travaria a tela.
      clearTimeout(this.timerAnalise);
      this.timerAnalise = setTimeout(() => {
        this.analise = analisarNumeros(this.form.numeros);
        this.agendarConferencia();
      }, 250);
    },
  },
  mounted() {
    this.carregar();
    this.timer = setInterval(() => { if (!document.hidden) this.carregar(true); }, ATUALIZAR_MS);
    try {
      const u = JSON.parse(localStorage.getItem('userdata') || '{}');
      socketClient.connect(u && u.id);
      this.offSocket.push(socketClient.on('disparo:atualizado', () => {
        clearTimeout(this.timerSocket);
        this.timerSocket = setTimeout(() => this.carregar(true), 400);
      }));
    } catch (_e) { /* a tela também se atualiza sozinha */ }
  },
  beforeUnmount() {
    clearInterval(this.timer);
    clearTimeout(this.timerAnalise);
    clearTimeout(this.timerSocket);
    clearTimeout(this.timerContagem);
    clearTimeout(this.timerConferencia);
    this.offSocket.forEach((fn) => { try { fn(); } catch (_e) { /* noop */ } });
    if (this.foto && this.foto.previewUrl) URL.revokeObjectURL(this.foto.previewUrl);
  },
  methods: {
    fmt(n) {
      return Number(n || 0).toLocaleString('pt-BR');
    },
    tamanhoLegivel(bytes) {
      const n = Number(bytes || 0);
      if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
      return `${Math.max(1, Math.round(n / 1024))} KB`;
    },
    dataHora(d) {
      if (!d) return '';
      return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    urlArquivo(relativo) {
      if (!relativo) return '';
      if (/^(https?:|data:|blob:)/i.test(relativo)) return relativo;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativo}`;
    },
    msgErro(e) {
      return (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Erro inesperado.';
    },

    async carregar(silencioso = false) {
      if (!silencioso) this.carregando = true;
      try {
        const { data } = await axios.get('/disparo/painel');
        this.painel = data;
        this.erroPainel = null;
      } catch (e) {
        this.erroPainel = this.msgErro(e);
      } finally {
        this.carregando = false;
      }
    },

    // ── Foto ──────────────────────────────────────────────────────────────
    escolherFoto() {
      if (this.$refs.inputFoto) {
        this.$refs.inputFoto.value = '';
        this.$refs.inputFoto.click();
      }
    },
    onFotoEscolhida(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (file) this.usarFoto(file);
    },
    onFotoSolta(event) {
      this.arrastando = false;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.usarFoto(file);
    },
    usarFoto(file) {
      this.fotoErro = null;
      const tipo = String(file.type || '').toLowerCase();
      if (/hei[cf]/i.test(`${tipo} ${file.name}`)) {
        this.fotoErro = 'Foto HEIC (padrão do iPhone) não abre no WhatsApp de todo mundo. Salve como JPG e escolha de novo.';
        return;
      }
      if (!TIPOS_FOTO.includes(tipo)) {
        this.fotoErro = 'Formato não aceito. Use foto JPG, PNG ou WEBP.';
        return;
      }
      if (file.size > MAX_FOTO_MB * 1024 * 1024) {
        this.fotoErro = `A foto tem ${this.tamanhoLegivel(file.size)}; o máximo é ${MAX_FOTO_MB} MB.`;
        return;
      }
      if (this.foto && this.foto.previewUrl) URL.revokeObjectURL(this.foto.previewUrl);
      this.foto = { file, previewUrl: URL.createObjectURL(file), nome: file.name, tamanho: file.size };
    },
    removerFoto() {
      if (this.foto && this.foto.previewUrl) URL.revokeObjectURL(this.foto.previewUrl);
      this.foto = null;
      this.fotoErro = null;
    },

    // ── Números ───────────────────────────────────────────────────────────
    escolherArquivoNumeros() {
      if (this.$refs.inputNumeros) {
        this.$refs.inputNumeros.value = '';
        this.$refs.inputNumeros.click();
      }
    },
    async onArquivoNumeros(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (/\.(xlsx?|ods)$/i.test(file.name)) {
        erro({
          title: 'Planilha do Excel',
          text: 'Abra a planilha, selecione a coluna dos telefones, copie (Ctrl+C) e cole no campo de números. Ou salve como CSV.',
        });
        return;
      }
      try {
        const texto = await file.text();
        this.form.numeros = this.form.numeros.trim() ? `${this.form.numeros.trim()}\n${texto}` : texto;
      } catch (e) {
        erro({ title: 'Não foi possível ler o arquivo.', text: this.msgErro(e) });
      }
    },
    limparNumeros() {
      this.form.numeros = '';
      this.analise = ANALISE_VAZIA();
      this.mostrarInvalidos = false;
      this.conferencia = null;
    },
    /** Pergunta ao servidor quantos números da lista já são sabidamente sem WhatsApp. */
    agendarConferencia() {
      clearTimeout(this.timerConferencia);
      if (!this.analise.validos.length) {
        this.seqConferencia += 1;
        this.conferencia = null;
        return;
      }
      this.timerConferencia = setTimeout(() => this.conferirNumeros(), 700);
    },
    async conferirNumeros() {
      this.seqConferencia += 1;
      const seq = this.seqConferencia;
      try {
        const { data } = await axios.post('/disparo/conferirNumeros', { numeros: this.form.numeros }, { timeout: 0 });
        if (seq === this.seqConferencia) this.conferencia = data;
      } catch (_e) {
        if (seq === this.seqConferencia) this.conferencia = null; // só informativo
      }
    },

    // ── Etiquetas do sistema ──────────────────────────────────────────────
    async carregarEtiquetas() {
      this.carregandoEtiquetas = true;
      this.erroEtiquetas = null;
      try {
        const { data } = await axios.get('/disparo/etiquetas');
        this.etiquetas = Array.isArray(data) ? data : [];
      } catch (e) {
        this.erroEtiquetas = `Não foi possível carregar as etiquetas: ${this.msgErro(e)}`;
      } finally {
        this.carregandoEtiquetas = false;
      }
    },
    agendarContagem() {
      clearTimeout(this.timerContagem);
      this.erroContagem = null;
      if (!this.selecao.incluir.length) {
        this.seqContagem += 1; // descarta resposta que ainda esteja a caminho
        this.contagemEtiquetas = null;
        this.contandoEtiquetas = false;
        return;
      }
      this.contandoEtiquetas = true;
      this.timerContagem = setTimeout(() => this.contarEtiquetas(), 400);
    },
    async contarEtiquetas() {
      this.seqContagem += 1;
      const seq = this.seqContagem;
      try {
        const { data } = await axios.post('/disparo/previewEtiquetas', {
          incluir: this.selecao.incluir,
          excluir: this.selecao.excluir,
        });
        if (seq !== this.seqContagem) return; // chegou depois de uma mais nova
        this.contagemEtiquetas = data;
      } catch (e) {
        if (seq !== this.seqContagem) return;
        this.contagemEtiquetas = null;
        this.erroContagem = `Não foi possível contar: ${this.msgErro(e)}`;
      } finally {
        if (seq === this.seqContagem) this.contandoEtiquetas = false;
      }
    },
    escaparHtml(texto) {
      return String(texto || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    },
    descreverEtiquetas(et) {
      if (!et) return '';
      const nomes = (lista) => (Array.isArray(lista) ? lista.map((e) => e.nome).filter(Boolean) : []);
      const incluir = nomes(et.incluir);
      const excluir = nomes(et.excluir);
      return `Etiquetas: ${incluir.join(', ')}${excluir.length ? ` · sem: ${excluir.join(', ')}` : ''}`;
    },

    // ── Envio para a fila ─────────────────────────────────────────────────
    confirmarEnvio() {
      if (!this.podeEnviar) return;
      let total;
      let quem;
      if (this.origem === 'etiquetas') {
        total = this.totalDestinatarios;
        const nomes = this.etiquetas
          .filter((e) => this.selecao.incluir.includes(e.id))
          .map((e) => this.escaparHtml(e.nome));
        quem = `<b>${this.fmt(total)}</b> contato(s) das etiquetas <b>${nomes.join(', ')}</b>`;
      } else {
        // Análise do que está no campo AGORA (o debounce pode estar pendente).
        this.analise = analisarNumeros(this.form.numeros);
        total = this.analise.validos.length;
        const pulados = this.conferencia && this.conferencia.validos === total ? this.conferencia.semWhatsapp : 0;
        quem = `<b>${this.fmt(total)}</b> número(s)${pulados ? ` (${this.fmt(pulados)} já sem WhatsApp serão pulados)` : ''}`;
      }
      if (!total) return;
      const naFrente = this.fila.pendentes;
      const conectados = this.chips ? this.chips.conectados : 0;
      const linhas = [
        `${quem} ${this.foto ? 'com foto' : 'só com texto'}.`,
        naFrente ? `Há ${this.fmt(naFrente)} mensagem(ns) na frente na fila.` : 'A fila está vazia: começa a sair já.',
        conectados ? `${conectados} chip(s) conectado(s) no rodízio.` : '<b>Nenhum chip de disparo conectado</b>: o envio fica esperando até conectar.',
      ];
      confirmar({
        options: {
          title: 'Colocar na fila?',
          html: linhas.join('<br>'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sim, colocar na fila',
          cancelButtonText: 'Voltar',
          customClass: { confirmButton: 'btn btn-success w-xs me-2', cancelButton: 'btn btn-light w-xs' },
          buttonsStyling: false,
        },
        confirmado: () => this.enviar(),
      });
    },
    async enviar() {
      this.enviando = true;
      try {
        let imagem = null;
        if (this.foto) {
          this.etapa = 'foto';
          const fd = new FormData();
          fd.append('file', this.foto.file);
          const { data } = await axios.post('/disparo/imagem', fd, { timeout: 0 });
          imagem = data;
        }
        this.etapa = 'fila';
        const corpo = { nome: this.form.nome.trim() || null, texto: this.form.texto, imagem };
        if (this.origem === 'etiquetas') {
          Object.assign(corpo, {
            origem: 'etiquetas',
            etiquetasIncluir: this.selecao.incluir,
            etiquetasExcluir: this.selecao.excluir,
          });
        } else {
          corpo.numeros = this.form.numeros;
        }
        const { data } = await axios.post('/disparo/lote', corpo, { timeout: 0 });
        const repetidos = data.duplicados ? ` (${this.fmt(data.duplicados)} repetido(s) entraram uma vez só)` : '';
        const pulados = data.semWhatsappConhecidos
          ? ` · ${this.fmt(data.semWhatsappConhecidos)} já sabidamente sem WhatsApp, pulado(s)`
          : '';
        sucesso({
          title: `Envio #${data.lote.id} na fila`,
          text: `${this.fmt(data.lote.total)} número(s)${repetidos}${pulados} · posição ${data.posicao} na fila`,
          timer: 4500,
        });
        this.removerFoto();
        this.form = { nome: '', texto: '', numeros: '' };
        this.selecao = { incluir: [], excluir: [] };
        this.contagemEtiquetas = null;
        this.conferencia = null;
        this.analise = ANALISE_VAZIA();
        this.mostrarInvalidos = false;
        this.carregar(true);
      } catch (e) {
        erro({
          title: this.etapa === 'foto' ? 'A foto não subiu' : 'Não foi possível colocar na fila',
          text: this.msgErro(e),
        });
      } finally {
        this.enviando = false;
        this.etapa = '';
      }
    },

    // ── Ações na fila ─────────────────────────────────────────────────────
    async acaoLote(lote, acao) {
      const executar = async () => {
        this.acaoEmAndamento = lote.id;
        try {
          await axios.post(`/disparo/${acao}/${lote.id}`);
          await this.carregar(true);
        } catch (e) {
          erro({ title: 'Não foi possível concluir.', text: this.msgErro(e) });
        } finally {
          this.acaoEmAndamento = null;
        }
      };
      if (acao !== 'cancelar') { executar(); return; }
      confirmar({
        options: {
          title: `Cancelar o envio #${lote.id}?`,
          text: `${this.fmt(lote.pendentes)} número(s) que ainda não receberam saem da fila. O que já saiu não volta.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, cancelar',
          cancelButtonText: 'Voltar',
          customClass: { confirmButton: 'btn btn-danger w-xs me-2', cancelButton: 'btn btn-light w-xs' },
          buttonsStyling: false,
        },
        confirmado: executar,
      });
    },
    async acaoTodos(acao) {
      this.acaoEmAndamento = acao;
      try {
        await axios.post(`/disparo/${acao}`);
        await this.carregar(true);
      } catch (e) {
        erro({ title: 'Não foi possível concluir.', text: this.msgErro(e) });
      } finally {
        this.acaoEmAndamento = null;
      }
    },
    async retomarChip(chip) {
      try {
        const { data } = await axios.post(`/canal/baileys/retomarDisparo/${chip.id}`);
        sucesso({
          title: `${chip.nome} de volta ao rodízio`,
          text: data && data.liberadoAte
            ? `Liberado até ${this.dataHora(data.liberadoAte)}: não volta para a espera sozinho. Se der erro, a mensagem passa para outro chip.`
            : false,
          timer: 3500,
        });
        await this.carregar(true);
      } catch (e) {
        erro({ title: 'Não foi possível retomar o chip.', text: this.msgErro(e) });
      }
    },
    async baixarCsv(lote) {
      try {
        const resposta = await axios.get(`/disparo/relatorio/${lote.id}`, { responseType: 'blob', timeout: 0 });
        const url = window.URL.createObjectURL(resposta.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `disparo-${lote.id}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        erro({ title: 'Não foi possível gerar a planilha.', text: this.msgErro(e) });
      }
    },

    // ── Apresentação ──────────────────────────────────────────────────────
    pct(lote) {
      if (!lote.total) return 0;
      return Math.min(100, Math.round((lote.enviados / lote.total) * 100));
    },
    naoEnviados(lote) {
      return lote.falhas + lote.sem_whatsapp + lote.bloqueados + lote.cancelados;
    },
    detalheNaoEnviados(lote) {
      const partes = [];
      if (lote.sem_whatsapp) partes.push(`${this.fmt(lote.sem_whatsapp)} sem WhatsApp`);
      if (lote.bloqueados) partes.push(`${this.fmt(lote.bloqueados)} pediram para sair`);
      if (lote.falhas) partes.push(`${this.fmt(lote.falhas)} falha(s)`);
      if (lote.cancelados) partes.push(`${this.fmt(lote.cancelados)} cancelado(s)`);
      return partes.join(' · ');
    },
    situacao(lote) {
      if (lote.status === 'na_fila') {
        const semChip = !this.chips || !this.chips.conectados;
        if (lote.posicao === 1 && semChip) return { rotulo: 'Aguardando chip', classe: 'bg-warning-subtle text-warning', icone: 'ri-sim-card-2-line' };
        if (lote.posicao === 1) return { rotulo: 'Enviando', classe: 'bg-success-subtle text-success', icone: 'ri-send-plane-2-line' };
        return { rotulo: `Na fila · ${lote.posicao}º`, classe: 'bg-info-subtle text-info', icone: 'ri-time-line' };
      }
      if (lote.status === 'pausado') return { rotulo: 'Pausado', classe: 'bg-warning-subtle text-warning', icone: 'ri-pause-circle-line' };
      if (lote.status === 'cancelado') return { rotulo: 'Cancelado', classe: 'bg-secondary-subtle text-secondary', icone: 'ri-close-circle-line' };
      return { rotulo: 'Concluído', classe: 'bg-primary-subtle text-primary', icone: 'ri-checkbox-circle-line' };
    },
    rotuloChip(c) {
      if (c.pausado) return c.pausadoAte ? `Pausado até ${this.dataHora(c.pausadoAte)}` : 'Pausado';
      if (c.conectado) return 'No rodízio';
      if (c.status === 'reconectando') return 'Reconectando';
      return c.status === 'banido' ? 'Banido' : 'Desconectado';
    },
    badgeChip(c) {
      if (c.pausado) return 'bg-warning-subtle text-warning';
      if (c.conectado) return 'bg-success-subtle text-success';
      if (c.status === 'reconectando') return 'bg-info-subtle text-info';
      return 'bg-danger-subtle text-danger';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Disparo rápido" pageTitle="Disparo rápido" />

    <div class="row g-3">
      <!-- ── Novo disparo ─────────────────────────────────────────────── -->
      <div class="col-xl-7">
        <div class="card mb-0 h-100">
          <div class="card-header">
            <h5 class="m-0"><i class="ri-send-plane-2-line me-1 text-primary"></i>Novo disparo</h5>
            <small class="text-muted">
              Mensagem, foto e os números da sua planilha. O envio entra no fim da fila única e sai pelos chips conectados.
            </small>
          </div>
          <div class="card-body">
            <!-- Mensagem -->
            <label for="disparoTexto" class="form-label fw-semibold mb-1">1. Mensagem</label>
            <textarea
              id="disparoTexto"
              v-model="form.texto"
              class="form-control"
              rows="5"
              :maxlength="MAX_TEXTO"
              placeholder="Escreva a mensagem. Com foto, ela vai como legenda."
            ></textarea>
            <div class="d-flex justify-content-between small text-muted mt-1 mb-3 gap-2">
              <span>No WhatsApp: *negrito*, _itálico_, ~riscado~</span>
              <span class="text-nowrap">{{ fmt(form.texto.length) }}/{{ fmt(MAX_TEXTO) }}</span>
            </div>
            <div v-if="legendaLonga" class="alert alert-warning py-2 px-3 small">
              <i class="ri-alert-line me-1"></i>Legenda de foto com mais de 1.024 caracteres pode ser cortada em alguns aparelhos.
            </div>

            <!-- Foto -->
            <label class="form-label fw-semibold mb-1">2. Foto <span class="text-muted fw-normal">(opcional)</span></label>
            <input
              ref="inputFoto"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="d-none"
              @change="onFotoEscolhida"
            />
            <div
              v-if="!foto"
              class="foto-area mb-1"
              :class="{ 'foto-area--arrastando': arrastando }"
              role="button"
              tabindex="0"
              @click="escolherFoto"
              @keydown.enter.prevent="escolherFoto"
              @dragover.prevent="arrastando = true"
              @dragleave.prevent="arrastando = false"
              @drop.prevent="onFotoSolta"
            >
              <i class="ri-image-add-line fs-3 d-block mb-1"></i>
              <span class="fw-semibold">Escolher foto</span>
              <small class="d-block text-muted">ou arraste aqui · JPG, PNG ou WEBP · até {{ MAX_FOTO_MB }} MB</small>
            </div>
            <div v-else class="foto-escolhida mb-1">
              <img :src="foto.previewUrl" alt="Foto escolhida" class="foto-miniatura" />
              <div class="flex-grow-1 min-w-0">
                <div class="fw-semibold text-truncate">{{ foto.nome }}</div>
                <small class="text-muted d-block">{{ tamanhoLegivel(foto.tamanho) }}<template v-if="foto.tamanho > 300 * 1024"> · será reduzida para envio em massa</template></small>
              </div>
              <div class="d-flex gap-1 flex-shrink-0">
                <button type="button" class="btn btn-sm btn-soft-primary" :disabled="enviando" @click="escolherFoto">Trocar</button>
                <button type="button" class="btn btn-sm btn-soft-danger" :disabled="enviando" title="Remover foto" @click="removerFoto">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
            <div v-if="fotoErro" class="text-danger small mb-1"><i class="ri-error-warning-line me-1"></i>{{ fotoErro }}</div>
            <div class="mb-3"></div>

            <!-- Quem recebe: planilha/manual ou etiquetas do sistema -->
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <span class="form-label fw-semibold m-0">3. Quem recebe</span>
              <div class="btn-group btn-group-sm origem-switch" role="group" aria-label="De onde vêm os números">
                <input id="origemLista" v-model="origem" type="radio" class="btn-check" value="lista" autocomplete="off" :disabled="enviando" />
                <label class="btn btn-outline-primary" for="origemLista"><i class="ri-file-list-3-line me-1"></i>Planilha / manual</label>
                <input id="origemEtiquetas" v-model="origem" type="radio" class="btn-check" value="etiquetas" autocomplete="off" :disabled="enviando" />
                <label class="btn btn-outline-primary" for="origemEtiquetas"><i class="ri-price-tag-3-line me-1"></i>Etiquetas do sistema</label>
              </div>
            </div>

            <template v-if="origem === 'lista'">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1">
                <label for="disparoNumeros" class="form-label small text-muted m-0">Cole os números ou carregue um CSV</label>
                <div class="d-flex gap-2">
                  <input ref="inputNumeros" type="file" accept=".csv,.txt,.xls,.xlsx,.ods,text/csv,text/plain" class="d-none" @change="onArquivoNumeros" />
                  <button type="button" class="btn btn-sm btn-soft-secondary" :disabled="enviando" @click="escolherArquivoNumeros">
                    <i class="ri-file-text-line me-1"></i>Carregar CSV
                  </button>
                  <button v-if="form.numeros" type="button" class="btn btn-sm btn-link text-muted p-0" :disabled="enviando" @click="limparNumeros">Limpar</button>
                </div>
              </div>
              <textarea
                id="disparoNumeros"
                v-model="form.numeros"
                class="form-control font-monospace numeros-campo"
                rows="7"
                spellcheck="false"
                placeholder="Copie a coluna de telefones da planilha e cole aqui (um por linha).&#10;Ex.: (31) 98765-4321"
              ></textarea>
              <div class="numeros-resumo small mt-1 mb-3">
                <span class="text-success fw-semibold"><i class="ri-checkbox-circle-line me-1"></i>{{ fmt(analise.validos.length) }} válido(s)</span>
                <span v-if="analise.duplicados" class="text-muted"> · {{ fmt(analise.duplicados) }} repetido(s), ignorado(s)</span>
                <span v-if="conferencia && conferencia.semWhatsapp && conferencia.validos === analise.validos.length" class="text-warning">
                  · {{ fmt(conferencia.semWhatsapp) }} já sem WhatsApp (serão pulados)
                </span>
                <span v-if="analise.invalidos" class="text-danger">
                  · {{ fmt(analise.invalidos) }} inválido(s)
                  <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="mostrarInvalidos = !mostrarInvalidos">
                    {{ mostrarInvalidos ? 'esconder' : 'ver' }}
                  </button>
                </span>
                <div v-if="mostrarInvalidos && analise.exemplosInvalidos.length" class="invalidos-lista mt-1">
                  <span v-for="(x, i) in analise.exemplosInvalidos" :key="i" class="badge bg-danger-subtle text-danger me-1 mb-1">{{ x }}</span>
                  <small v-if="analise.invalidos > analise.exemplosInvalidos.length" class="text-muted d-block">… e mais {{ fmt(analise.invalidos - analise.exemplosInvalidos.length) }}.</small>
                </div>
              </div>
            </template>

            <template v-else>
              <div v-if="carregandoEtiquetas" class="text-muted small mb-3">
                <span class="spinner-border spinner-border-sm me-1"></span>Carregando etiquetas…
              </div>
              <div v-else-if="erroEtiquetas" class="text-danger small mb-3">
                <i class="ri-error-warning-line me-1"></i>{{ erroEtiquetas }}
                <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="carregarEtiquetas">tentar de novo</button>
              </div>
              <div v-else-if="!etiquetas.length" class="alert alert-info small mb-3">
                <i class="ri-price-tag-3-line me-1"></i>Nenhuma etiqueta cadastrada ainda. Crie em <strong>Cadastros → Etiquetas</strong> e marque os contatos.
              </div>
              <template v-else>
                <label class="form-label small text-muted mb-1">Contatos com qualquer uma destas etiquetas</label>
                <Multiselect
                  v-model="selecao.incluir"
                  :options="opcoesIncluir"
                  mode="tags"
                  :searchable="true"
                  :close-on-select="false"
                  :disabled="enviando"
                  placeholder="Escolha as etiquetas"
                  no-results-text="Nenhuma etiqueta encontrada"
                  no-options-text="Nenhuma etiqueta"
                />
                <label class="form-label small text-muted mb-1 mt-2">Tirar quem tiver <span class="fw-normal">(opcional)</span></label>
                <Multiselect
                  v-model="selecao.excluir"
                  :options="opcoesExcluir"
                  mode="tags"
                  :searchable="true"
                  :close-on-select="false"
                  :disabled="enviando"
                  placeholder="Nenhuma"
                  no-results-text="Nenhuma etiqueta encontrada"
                  no-options-text="Nenhuma etiqueta"
                />
                <div class="numeros-resumo small mt-2 mb-3">
                  <span v-if="!selecao.incluir.length" class="text-muted">Escolha ao menos uma etiqueta.</span>
                  <span v-else-if="!contagemEtiquetas && contandoEtiquetas" class="text-muted">
                    <span class="spinner-border spinner-border-sm me-1"></span>Contando contatos…
                  </span>
                  <template v-else-if="contagemEtiquetas">
                    <span class="text-success fw-semibold"><i class="ri-checkbox-circle-line me-1"></i>{{ fmt(contagemEtiquetas.elegiveis) }} contato(s) vão receber</span>
                    <span v-if="contagemEtiquetas.optOut" class="text-muted"> · {{ fmt(contagemEtiquetas.optOut) }} pediram para sair</span>
                    <span v-if="contagemEtiquetas.semTelefone" class="text-muted"> · {{ fmt(contagemEtiquetas.semTelefone) }} sem telefone</span>
                    <span v-if="contandoEtiquetas" class="spinner-border spinner-border-sm ms-1 text-muted" role="status"></span>
                  </template>
                  <div v-if="erroContagem" class="text-danger mt-1">{{ erroContagem }}</div>
                  <small class="d-block text-muted mt-1">
                    Só contatos ativos. A lista é montada na hora de colocar na fila; o mesmo número entra uma vez só.
                  </small>
                </div>
              </template>
            </template>

            <!-- Nome -->
            <label for="disparoNome" class="form-label fw-semibold mb-1">4. Nome do envio <span class="text-muted fw-normal">(opcional)</span></label>
            <input
              id="disparoNome"
              v-model="form.nome"
              type="text"
              class="form-control mb-3"
              maxlength="191"
              placeholder="Ex.: Convite ato 02/10 — Zona Norte"
            />

            <button type="button" class="btn btn-primary btn-lg w-100" :disabled="!podeEnviar" @click="confirmarEnvio">
              <template v-if="enviando">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ etapa === 'foto' ? 'Enviando a foto…' : 'Colocando na fila…' }}
              </template>
              <template v-else>
                <i class="ri-send-plane-2-line me-1"></i>Colocar na fila<template v-if="totalDestinatarios"> · {{ fmt(totalDestinatarios) }} {{ origem === 'etiquetas' ? 'contato(s)' : 'número(s)' }}</template>
              </template>
            </button>
            <small class="text-muted d-block mt-2">
              Nada sai antes de você confirmar. Quem pediu para sair (SAIR, PARAR) é pulado;
              quem responder vira contato com opt-in. Número já verificado sem WhatsApp é pulado
              sem gastar chip.
            </small>
          </div>
        </div>
      </div>

      <!-- ── Prévia + chips ───────────────────────────────────────────── -->
      <div class="col-xl-5 d-flex flex-column gap-3">
        <div class="card mb-0">
          <div class="card-header py-2">
            <h6 class="m-0"><i class="ri-smartphone-line me-1 text-muted"></i>Como vai chegar</h6>
          </div>
          <div class="card-body wa-fundo">
            <div v-if="form.texto.trim() || foto" class="wa-bolha">
              <img v-if="foto" :src="foto.previewUrl" alt="Prévia da foto" class="wa-foto" />
              <div v-if="form.texto.trim()" class="wa-texto">{{ form.texto }}</div>
              <div class="wa-hora">{{ horaAgora }} <i class="ri-check-double-line"></i></div>
            </div>
            <div v-else class="text-muted small text-center py-4">
              Escreva a mensagem ou escolha a foto para ver a prévia.
            </div>
          </div>
        </div>

        <div class="card mb-0 flex-grow-1">
          <div class="card-header py-2 d-flex justify-content-between align-items-center gap-2">
            <h6 class="m-0"><i class="ri-sim-card-2-line me-1 text-muted"></i>Chips no rodízio</h6>
            <router-link to="/app/cadastros/canais-baileys" class="btn btn-sm btn-soft-primary">
              <i class="ri-qr-code-line me-1"></i>Conectar chips
            </router-link>
          </div>
          <div class="card-body">
            <div v-if="carregando && !painel" class="text-muted small"><span class="spinner-border spinner-border-sm me-1"></span>Carregando…</div>
            <div v-else-if="erroPainel && !painel" class="text-danger small">{{ erroPainel }}</div>
            <template v-else-if="chips">
              <div v-if="!chips.totalChips" class="alert alert-warning small mb-0">
                <i class="ri-alert-line me-1"></i>Nenhum chip de disparo ainda. Em <strong>Cadastros → Canais API-B</strong>, use
                <strong>Conectar chips de disparo</strong>. A fila espera e começa a sair assim que o primeiro conectar.
              </div>
              <template v-else>
                <div class="row g-2 text-center mb-2">
                  <div class="col-4">
                    <div class="kpi"><small class="text-muted d-block">Conectados</small><span class="fs-5 fw-semibold text-success">{{ chips.conectados }}</span><small class="text-muted"> de {{ chips.totalChips }}</small></div>
                  </div>
                  <div class="col-4">
                    <div class="kpi"><small class="text-muted d-block">Pausados</small><span class="fs-5 fw-semibold" :class="chips.pausados ? 'text-warning' : ''">{{ chips.pausados }}</span></div>
                  </div>
                  <div class="col-4">
                    <div class="kpi"><small class="text-muted d-block">Vazão</small><span class="fs-5 fw-semibold">~{{ fmt(chips.vazaoPorHora) }}</span><small class="text-muted">/h</small></div>
                  </div>
                </div>
                <div class="small mb-2">
                  <strong>{{ fmt(fila.pendentes) }}</strong> mensagem(ns) na fila
                  <template v-if="chips.horasParaTerminar != null && fila.pendentes"> · ~{{ String(chips.horasParaTerminar).replace('.', ',') }} h para esvaziar</template>
                  <template v-if="fila.pendentesPausados"> · {{ fmt(fila.pendentesPausados) }} em envios pausados</template>
                </div>
                <div class="chips-lista">
                  <div v-for="c in chipsOrdenados" :key="c.id" class="chip-linha">
                    <div class="min-w-0">
                      <div class="fw-semibold text-truncate">{{ c.nome }}</div>
                      <small class="text-muted">{{ c.telefone || '—' }}</small>
                      <small v-if="c.pausado && c.ultimoErro" class="d-block text-warning chip-motivo">{{ c.ultimoErro }}</small>
                      <small v-else-if="c.liberadoManualAte" class="d-block text-info chip-motivo">
                        Liberado por você até {{ dataHora(c.liberadoManualAte) }} — não volta para a espera sozinho<template v-if="c.ultimoErro"> ({{ c.ultimoErro }})</template>
                      </small>
                    </div>
                    <div class="text-end flex-shrink-0">
                      <span class="badge" :class="badgeChip(c)">{{ rotuloChip(c) }}</span>
                      <small class="d-block text-muted">{{ fmt(c.enviadosHoje) }} hoje<template v-if="c.falhasHoje"> · <span class="text-danger">{{ c.falhasHoje }} falha(s)</span></template></small>
                      <button v-if="c.pausado" type="button" class="btn btn-link btn-sm p-0" :title="c.ultimoErro || ''" @click="retomarChip(c)">Retomar</button>
                    </div>
                  </div>
                </div>
                <small class="text-muted d-block mt-2">
                  Chip marcado como <strong>Disparo</strong> entra no rodízio sozinho assim que conecta. Quem
                  <strong>menos disparou hoje</strong> recebe primeiro; cada chip espera ~{{ fmt(chips.intervaloMedioS) }}s entre
                  mensagens. Se der erro ou o WhatsApp recusar, a mensagem <strong>passa para outro chip</strong>. Chip com
                  3 falhas seguidas ou restrito vai para a espera — e o que você retomar não volta para ela sozinho até o fim do dia.
                </small>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Fila ─────────────────────────────────────────────────────────── -->
    <div class="card mt-3">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 class="m-0"><i class="ri-list-ordered me-1 text-primary"></i>Fila de disparo</h5>
          <small class="text-muted">Os envios saem na ordem em que entraram. O primeiro da fila está saindo agora.</small>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button
            v-if="fila.lotesNaFila"
            type="button"
            class="btn btn-sm btn-soft-warning"
            :disabled="!!acaoEmAndamento"
            @click="acaoTodos('pausarTudo')"
          >
            <i class="ri-pause-line me-1"></i>Pausar tudo
          </button>
          <button
            v-if="temPausados"
            type="button"
            class="btn btn-sm btn-soft-success"
            :disabled="!!acaoEmAndamento"
            @click="acaoTodos('retomarTudo')"
          >
            <i class="ri-play-line me-1"></i>Retomar tudo
          </button>
          <button type="button" class="btn btn-sm btn-light" title="Atualizar" @click="carregar(true)">
            <i class="ri-refresh-line"></i>
          </button>
        </div>
      </div>
      <div class="card-body p-0">
        <div v-if="!lotes.length" class="text-center text-muted py-5">
          <i class="ri-inbox-line fs-2 d-block mb-1"></i>
          Nenhum envio ainda. Monte o primeiro aí em cima.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm align-middle mb-0 fila-tabela">
            <thead>
              <tr>
                <th>Envio</th>
                <th>Situação</th>
                <th class="col-progresso">Enviados</th>
                <th class="text-end">Entregues</th>
                <th class="text-end">Lidos</th>
                <th class="text-end">Respostas</th>
                <th class="text-end">Não enviados</th>
                <th class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="l in lotes" :key="l.id">
                <td class="col-envio">
                  <div class="d-flex align-items-center gap-2">
                    <img v-if="l.imagem && l.imagem.url" :src="urlArquivo(l.imagem.url)" alt="" class="lote-miniatura" loading="lazy" />
                    <span v-else class="lote-miniatura lote-miniatura--texto"><i class="ri-chat-3-line"></i></span>
                    <div class="min-w-0">
                      <div class="fw-semibold text-truncate">#{{ l.id }} · {{ l.nome }}</div>
                      <small class="text-muted d-block text-truncate">{{ dataHora(l.createdAt || l.created_at) }}<template v-if="l.texto"> · {{ l.texto }}</template></small>
                      <small v-if="l.origem === 'etiquetas' && l.etiquetas" class="text-muted d-block text-truncate" :title="descreverEtiquetas(l.etiquetas)">
                        <i class="ri-price-tag-3-line me-1"></i>{{ descreverEtiquetas(l.etiquetas) }}
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="situacao(l).classe"><i :class="situacao(l).icone" class="me-1"></i>{{ situacao(l).rotulo }}</span>
                  <small v-if="l.ultimo_erro" class="d-block text-danger mt-1 erro-lote">{{ l.ultimo_erro }}</small>
                </td>
                <td class="col-progresso">
                  <div class="progress progress-sm mb-1">
                    <div class="progress-bar bg-success" :style="{ width: `${pct(l)}%` }"></div>
                  </div>
                  <small class="text-nowrap">
                    <strong>{{ fmt(l.enviados) }}</strong> de {{ fmt(l.total) }} ({{ pct(l) }}%)
                    <template v-if="l.pendentes && ['na_fila', 'pausado'].includes(l.status)"><span class="text-muted"> · faltam {{ fmt(l.pendentes) }}</span></template>
                  </small>
                </td>
                <td class="text-end">{{ fmt(l.entregues) }}</td>
                <td class="text-end">{{ fmt(l.lidos) }}</td>
                <td class="text-end">{{ fmt(l.respostas) }}</td>
                <td class="text-end">
                  <span :class="naoEnviados(l) ? 'text-danger' : 'text-muted'" :title="detalheNaoEnviados(l)">{{ fmt(naoEnviados(l)) }}</span>
                  <small v-if="naoEnviados(l)" class="d-block text-muted text-nowrap">{{ detalheNaoEnviados(l) }}</small>
                </td>
                <td class="text-end text-nowrap">
                  <button
                    v-if="l.status === 'na_fila'"
                    type="button"
                    class="btn btn-sm btn-soft-warning"
                    title="Pausar"
                    :disabled="acaoEmAndamento === l.id"
                    @click="acaoLote(l, 'pausar')"
                  ><i class="ri-pause-line"></i></button>
                  <button
                    v-if="l.status === 'pausado'"
                    type="button"
                    class="btn btn-sm btn-soft-success"
                    title="Retomar"
                    :disabled="acaoEmAndamento === l.id"
                    @click="acaoLote(l, 'retomar')"
                  ><i class="ri-play-line"></i></button>
                  <button
                    v-if="['na_fila', 'pausado'].includes(l.status)"
                    type="button"
                    class="btn btn-sm btn-soft-danger ms-1"
                    title="Cancelar o que ainda não saiu"
                    :disabled="acaoEmAndamento === l.id"
                    @click="acaoLote(l, 'cancelar')"
                  ><i class="ri-close-line"></i></button>
                  <button
                    type="button"
                    class="btn btn-sm btn-soft-secondary ms-1"
                    title="Baixar planilha (CSV) com a situação de cada número"
                    @click="baixarCsv(l)"
                  ><i class="ri-file-excel-2-line"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.min-w-0 { min-width: 0; }

/* Foto */
.foto-area {
  border: 2px dashed var(--vz-border-color);
  border-radius: 10px;
  padding: 1.1rem;
  text-align: center;
  cursor: pointer;
  color: var(--vz-secondary-color);
  background: var(--vz-light-bg-subtle, var(--vz-body-bg));
  transition: border-color 0.15s, background 0.15s;
}
.foto-area:hover,
.foto-area:focus,
.foto-area--arrastando {
  border-color: var(--vz-primary);
  color: var(--vz-body-color);
  outline: none;
}
.foto-escolhida {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid var(--vz-border-color);
  border-radius: 10px;
  padding: 0.5rem;
}
.foto-miniatura {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
  background: var(--vz-body-bg);
}

/* Quem recebe */
.origem-switch .btn { white-space: nowrap; }

/* Números */
.numeros-campo { font-size: 0.85rem; }
.invalidos-lista { max-height: 120px; overflow-y: auto; }

/* Prévia estilo WhatsApp */
.wa-fundo {
  background: #efeae2;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  min-height: 160px;
}
.wa-bolha {
  margin-left: auto;
  max-width: 85%;
  width: fit-content;
  background: #d9fdd3;
  color: #111b21;
  border-radius: 8px 0 8px 8px;
  padding: 4px;
  box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}
.wa-foto {
  display: block;
  max-width: 100%;
  max-height: 320px;
  border-radius: 6px;
  object-fit: cover;
}
.wa-texto {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9rem;
  padding: 4px 6px 0;
}
.wa-hora {
  text-align: right;
  font-size: 0.68rem;
  color: #667781;
  padding: 0 6px 2px;
}
.wa-hora i { color: #53bdeb; }

/* Chips */
.kpi {
  padding: 0.4rem;
  border-radius: 8px;
  border: 1px solid var(--vz-border-color);
  background: var(--vz-card-bg, var(--vz-body-bg));
}
.chips-lista {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--vz-border-color);
  border-radius: 8px;
}
.chip-linha {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 0.6rem;
  border-bottom: 1px solid var(--vz-border-color);
  font-size: 0.85rem;
}
.chip-linha:last-child { border-bottom: 0; }
.chip-motivo { white-space: normal; line-height: 1.25; }

/* Fila */
.fila-tabela th { white-space: nowrap; font-size: 0.78rem; }
.col-envio { min-width: 220px; max-width: 320px; }
.col-progresso { min-width: 190px; }
.progress-sm { height: 6px; }
.lote-miniatura {
  width: 38px;
  height: 38px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background: var(--vz-body-bg);
}
.lote-miniatura--texto {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--vz-secondary-color);
  border: 1px solid var(--vz-border-color);
}
.erro-lote { max-width: 220px; white-space: normal; }

/* Modo escuro */
[data-bs-theme="dark"] .foto-area { background: rgba(255, 255, 255, 0.02); }
[data-bs-theme="dark"] .wa-fundo { background: #0b141a; }
[data-bs-theme="dark"] .wa-bolha { background: #005c4b; color: #e9edef; box-shadow: none; }
[data-bs-theme="dark"] .wa-hora { color: rgba(233, 237, 239, 0.6); }
[data-bs-theme="dark"] .kpi { background: rgba(255, 255, 255, 0.02); }
/* No tema da marca, o texto destes selos fica escuro demais sobre o fundo escuro. */
[data-bs-theme="dark"] .badge.text-warning { color: #f2c94c !important; }
[data-bs-theme="dark"] .badge.text-primary { color: #f1757b !important; }
[data-bs-theme="dark"] .badge.text-secondary { color: #adb5bd !important; }
[data-bs-theme="dark"] .badge.text-danger { color: #f1757b !important; }
/* Opção não escolhida da chave: o vermelho da marca some no fundo escuro. */
[data-bs-theme="dark"] .origem-switch .btn-outline-primary {
  --vz-btn-color: #f1757b;
  --vz-btn-border-color: #f1757b;
}
</style>
